import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Chip,
  Alert,
  Divider,
  CardMedia,
  Switch,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import EditIcon from "@mui/icons-material/Edit";
import { VideoSource, SocialPlatform } from "../Enums/enums";
import MRSSThumbnailCreator from "../components/MRSSThumbnailCreator";
import VideoEditDialog from "../components/VideoEditDialog";
import axios from "axios";
import { toast } from "react-toastify";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";

/**
 * Step 2: Video Source Selection
 * Choose between Original video or Local upload for each selected video
 */
function VideoSourceSelection({
  selectedVideos = [],
  videoSources = {},
  handleVideoSourceChange,
  handleThumbnailChange,
  handleVideoSelection,
  videoPlatforms = {},
  handlePlatformChange,
  hasSocialFeeds = false,
  videoConvertToVertical = {},
  handleConvertToVerticalChange,
}) {
  const [selectedVideoKey, setSelectedVideoKey] = useState(
    selectedVideos[0]?.unique_key
  );
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
  const [videoEditDialogOpen, setVideoEditDialogOpen] = useState(false);

  const handleSourceChange = (uniqueKey) => (event) => {
    const source = event.target.value;
    if (source === VideoSource.ORIGINAL) {
      const original_thumbnail_url = currentVideo?.thumbnail;
      handleThumbnailChange(uniqueKey, original_thumbnail_url);
      handleVideoSourceChange(uniqueKey, { source, file: null });
    } else if (source === VideoSource.EDITED) {
      handleVideoSourceChange(uniqueKey, { source, file: null, renderedUrl: null });
      setVideoEditDialogOpen(true);
    } else {
      handleVideoSourceChange(uniqueKey, { source, file: null });
    }
  };

  const handleEditSave = (renderPayload) => {
    handleVideoSourceChange(selectedVideoKey, {
      source: VideoSource.EDITED,
      file: null,
      renderedUrl: null,
      renderPayload,
    });
  };

  const handleFileUpload = (uniqueKey) => (event) => {
    const file = event.target.files[0];
    if (file) {
      handleVideoSourceChange(uniqueKey, {
        source: VideoSource.LOCAL,
        file,
      });
    }
  };

  const handleThumbnailUpload = (uniqueKey) => (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      // Convert uploaded file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = reader.result;

        // Also save to parent state
        handleThumbnailChange(uniqueKey, base64String);
      };
      reader.onerror = () => {
        toast.error("Failed to load thumbnail");
      };
    }
  };

  const handleThumbnailDelete = (uniqueKey) => {
    // Clear the thumbnail from parent state
    handleThumbnailChange(uniqueKey, null);

  };

  const handleOpenThumbnailCreator = () => {
    setThumbnailDialogOpen(true);
  };

  const handleThumbnailFromCreator = (base64Thumbnail) => {
    // Ensure prefix is included
    const thumbnailWithPrefix = base64Thumbnail.startsWith("data:image")
      ? base64Thumbnail
      : `data:image/jpeg;base64,${base64Thumbnail}`;

    // Also update parent state if needed
    handleThumbnailChange(selectedVideoKey, thumbnailWithPrefix);
  };


  const fetchVideoDetails = async (unique_key) => {
    try {
      const response = await axios.get(process.env.REACT_APP_WEBSITE + `/apis/VideoDetail/${unique_key}`)

      if (response.data && response.data.data) {
        const details = response.data.data;
        return details;
      }
    } catch (error) {
      console.error("Error fetching video details:", error);
      toast.error("Failed to load video details");
    } finally {

    }
  };


  const currentVideo =
    selectedVideos.find((v) => v.unique_key === selectedVideoKey) ||
    selectedVideos[0];
  const currentSource = videoSources[selectedVideoKey];

  const getVideoUrl = (videoKey) => {
    const source = videoSources[videoKey];
    if (source?.source === VideoSource.LOCAL && source?.file) {
      return URL.createObjectURL(source.file);
    }
    if (source?.source === VideoSource.EDITED && source?.renderedUrl) {
      return source.renderedUrl;
    }
    const video = selectedVideos.find((v) => v.unique_key === videoKey);
    return video?.url || '';
  };

  const isSourceComplete = (src) => {
    if (!src) return false;
    if (src.source === VideoSource.LOCAL) return !!src.file;
    if (src.source === VideoSource.EDITED) return !!src.renderedUrl || !!src.renderPayload;
    return src.source === VideoSource.ORIGINAL;
  };

  const allConfigured = selectedVideos.every((video) => {
    const src = videoSources[video.unique_key];
    return (
      isSourceComplete(src) &&
      (!hasSocialFeeds || videoPlatforms[video.unique_key]?.length > 0)
    );
  });

  useEffect(() => {
    const fetchDetails = async () => {
      for (const v of selectedVideos) {
        // Only fetch if URL doesn't exist
        if (!v.url) {
          const videoDetails = await fetchVideoDetails(v.unique_key);
          if (videoDetails) {
            const thumbnail = videoDetails.thumbnail?.replace("https://wooglobe.s3.us-west-2.amazonaws.com", "https://dvkno8lo5mn14.cloudfront.net");
            const url = videoDetails.url?.replace("https://wooglobe.s3.us-west-2.amazonaws.com", "https://dvkno8lo5mn14.cloudfront.net");
            const tags = videoDetails.tags ? videoDetails.tags?.split(",") : []
            const data = {
              url: url,
              keywords: tags,
              thumbnail: thumbnail
            };
            handleVideoSelection(v.unique_key, data);
          }
        }
      }
    };

    if (selectedVideos && selectedVideos.length > 0) {
      fetchDetails();
    }
  }, [selectedVideos.length]); // Only run when video count changes

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Video Sources
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        For each selected video, choose to use the original video or upload a
        new version
      </Typography>

      {allConfigured && (
        <Alert severity="success" sx={{ mb: 3 }}>
          All videos configured! You can proceed to the next step.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Video List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, maxHeight: 600, overflow: "auto" }}>
            <Typography variant="subtitle1" gutterBottom>
              Videos ({selectedVideos.length})
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {selectedVideos.map((video) => {
                const src = videoSources[video.unique_key];
                const configured =
                  isSourceComplete(src) &&
                  (!hasSocialFeeds || videoPlatforms[video.unique_key]?.length > 0);
                return (
                  <Paper
                    key={video.unique_key}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor:
                        selectedVideoKey === video.unique_key
                          ? "primary.main"
                          : "transparent",
                      bgcolor:
                        selectedVideoKey === video.unique_key
                          ? "primary.50"
                          : "grey.50",
                    }}
                    onClick={() => setSelectedVideoKey(video.unique_key)}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          {video.unique_key}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {video.title || video.video_title}
                        </Typography>
                      </Box>
                      {configured && (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Right: Source Configuration */}
        <Grid item xs={12} md={8}>
          {currentVideo && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {currentVideo.unique_key}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                {currentVideo.title || currentVideo.video_title}
              </Typography>

              {/* Video Preview */}
              <Box
                mb={3}
                sx={{
                  height: 300,
                  bgcolor: "grey.100",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <video
                  controls
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                  src={currentVideo.url || currentVideo.url}
                />
              </Box>

              {/* Source Selection */}
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle1" gutterBottom>
                  Choose Video Source
                </Typography>
                <RadioGroup
                  value={currentSource?.source || ""}
                  onChange={handleSourceChange(currentVideo.unique_key)}
                >
                  {/* Original Video Option */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      border: "2px solid",
                      borderColor:
                        currentSource?.source === VideoSource.ORIGINAL
                          ? "primary.main"
                          : "grey.300",
                    }}
                  >
                    <FormControlLabel
                      value={VideoSource.ORIGINAL}
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <VideoLibraryIcon />
                          <Box>
                            <Typography variant="body1">
                              Use Original Video
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Use the existing video from your library
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    {currentSource?.source === VideoSource.ORIGINAL && (
                      <Box mt={1} ml={4}>
                        <Chip
                          label="Original video will be used"
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* Local Upload Option */}
                  <Paper
                    sx={{
                      p: 2,
                      border: "2px solid",
                      borderColor:
                        currentSource?.source === VideoSource.LOCAL
                          ? "primary.main"
                          : "grey.300",
                    }}
                  >
                    <FormControlLabel
                      value={VideoSource.LOCAL}
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CloudUploadIcon />
                          <Box>
                            <Typography variant="body1">
                              Upload from Local
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Upload a different version of this video
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    {currentSource?.source === VideoSource.LOCAL && (
                      <Box mt={2} ml={4}>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                        >
                          {currentSource?.file
                            ? "Change File"
                            : "Select Video File"}
                          <input
                            type="file"
                            hidden
                            accept="video/*"
                            onChange={handleFileUpload(currentVideo.unique_key)}
                          />
                        </Button>
                        {currentSource?.file && (
                          <Box mt={1}>
                            <Chip
                              label={currentSource.file.name}
                              color="success"
                              onDelete={() =>
                                handleVideoSourceChange(
                                  currentVideo.unique_key,
                                  {
                                    source: VideoSource.LOCAL,
                                    file: null,
                                  }
                                )
                              }
                            />
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              mt={1}
                            >
                              Size:{" "}
                              {(currentSource.file.size / (1024 * 1024)).toFixed(
                                2
                              )}{" "}
                              MB
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>

                  {/* Edit Content Option */}
                  <Paper
                    sx={{
                      p: 2,
                      mt: 2,
                      border: "2px solid",
                      borderColor:
                        currentSource?.source === VideoSource.EDITED
                          ? "primary.main"
                          : "grey.300",
                    }}
                  >
                    <FormControlLabel
                      value={VideoSource.EDITED}
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <MovieFilterIcon />
                          <Box>
                            <Typography variant="body1">
                              Edit Content
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Add captions &amp; styling using Video Editor templates
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    {currentSource?.source === VideoSource.EDITED && (
                      <Box mt={2} ml={4} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => setVideoEditDialogOpen(true)}
                        >
                          {currentSource?.renderedUrl ? "Re-edit Video" : currentSource?.renderPayload ? "Re-configure Edit" : "Open Editor"}
                        </Button>
                        {currentSource?.renderedUrl ? (
                          <Chip icon={<CheckCircleIcon />} label="Edited video ready" color="success" />
                        ) : currentSource?.renderPayload ? (
                          <Chip icon={<CheckCircleIcon />} label="Edit configured" color="primary" size="small" />
                        ) : (
                          <Chip label="Not yet configured" color="warning" size="small" />
                        )}
                      </Box>
                    )}
                  </Paper>
                </RadioGroup>
              </FormControl>

              {/* Thumbnail Selection */}
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Select Thumbnail (Optional)
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Choose a thumbnail for this video
                </Typography>
                {/* Thumbnail Preview */}
                <Box sx={{ my: 2 }}>
                  <Box mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      Selected Thumbnail:
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        position: "relative",
                        maxWidth: 200,
                      }}
                    >
                      {currentVideo?.thumbnail ? (
                        <>
                          <CardMedia
                            component="img"
                            image={currentVideo.thumbnail}
                            alt="Thumbnail preview"
                            sx={{
                              width: "100%",
                              height: "auto",
                              borderRadius: 1,
                              border: "2px solid",
                              borderColor: "primary.main",
                            }}
                          />
                          <Chip
                            label={
                              currentVideo.thumbnail.startsWith("data:image")
                                ? "Custom Thumbnail"
                                : "Default Thumbnail"
                            }
                            size="small"
                            color="success"
                            onDelete={() =>
                              handleThumbnailDelete(currentVideo.unique_key)
                            }
                            sx={{ mt: 1 }}
                          />
                        </>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 120,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "2px dashed",
                            borderColor: "grey.400",
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No Thumbnail
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  {/* Manual Upload */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        border: "2px solid",
                        borderColor: currentVideo.unique_key
                          ? "primary.main"
                          : "grey.300",
                        textAlign: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<ImageIcon />}
                        fullWidth
                      >
                        Upload Thumbnail
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleThumbnailUpload(
                            currentVideo.unique_key
                          )}
                        />
                      </Button>
                      <Typography variant="caption" color="textSecondary" mt={1}>
                        Upload image from your computer
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Generate from Video */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        border: "2px solid",
                        borderColor: "grey.300",
                        textAlign: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        onClick={handleOpenThumbnailCreator}
                        fullWidth
                      >
                        Capture from Video
                      </Button>
                      <Typography variant="caption" color="textSecondary" mt={1}>
                        Generate thumbnail from video frame
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

              </Box>

              {/* Vertical Conversion */}
              <Divider sx={{ my: 3 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1">Convert to Vertical (9:16)</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Add blur background and reformat to portrait for social media
                  </Typography>
                </Box>
                <Switch
                  checked={videoConvertToVertical[currentVideo.unique_key] || false}
                  onChange={(e) =>
                    handleConvertToVerticalChange(currentVideo.unique_key, e.target.checked)
                  }
                />
              </Box>

              {/* Platform Selection (only shown when social feeds exist) */}
              {hasSocialFeeds && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Select Publishing Platforms
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      Choose which social media platforms this video can be published on
                    </Typography>
                    <FormGroup row>
                      {Object.values(SocialPlatform).map((platform) => {
                        const currentPlatforms =
                          videoPlatforms[currentVideo.unique_key] || [];
                        return (
                          <FormControlLabel
                            key={platform}
                            control={
                              <Checkbox
                                checked={currentPlatforms.includes(platform)}
                                onChange={() => {
                                  const updated = currentPlatforms.includes(platform)
                                    ? currentPlatforms.filter((p) => p !== platform)
                                    : [...currentPlatforms, platform];
                                  handlePlatformChange(
                                    currentVideo.unique_key,
                                    updated
                                  );
                                }}
                                size="small"
                              />
                            }
                            label={
                              platform.charAt(0) +
                              platform.slice(1).toLowerCase()
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Video Edit Dialog */}
      {currentVideo && (
        <VideoEditDialog
          open={videoEditDialogOpen}
          onClose={() => setVideoEditDialogOpen(false)}
          video={currentVideo}
          onSave={handleEditSave}
          onThumbnailSave={handleThumbnailFromCreator}
        />
      )}

      {/* MRSS Thumbnail Creator Dialog */}
      {currentVideo && (
        <MRSSThumbnailCreator
          open={thumbnailDialogOpen}
          setOpen={setThumbnailDialogOpen}
          url={getVideoUrl(selectedVideoKey)}
          onSave={handleThumbnailFromCreator}
        />
      )}
    </Box>
  );
}

export default VideoSourceSelection;
